import React, { useMemo, useRef } from 'react';

import { getRenderingEngine } from '@cornerstonejs/core';
import { useSortable } from '@dnd-kit/sortable';
import { useLingui } from '@lingui/react/macro';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAtomValue } from 'jotai';

import { usePreferences } from '../../../../react-query/usePreferences';
import type { ImageFinding } from '../../../../utils/claimSummary/imageFindings';
import { getImageFindingsFromImageQuality } from '../../../../utils/claimSummary/imageFindings';
import { renderingEngineAtom } from '../../../../utils/cornerstone/cornerstoneAtoms';
import { RENDERING_ENGINE } from '../../../../utils/cornerstone/initializeCornerstone';
import AnnotatorConstants from '../../annotator.constants';
import { imageQualityAtom } from '../../atoms';
import { ImageFindingBadge } from '../findingViewer/imageBrowser/imageBucket/image/ImageFindingBadge';
import { useImageFindingOverlay } from '../hooks/useImageFindingOverlay';
import { showImageQualityFindingsAtom } from '../imageToolbar/atoms';

const useStyles = makeStyles(() => ({
  centerBorderedBox: {
    border: '1px solid gray',
    position: 'relative',
  },
  findingViewerContainer: {
    position: 'relative',
    '&.dragging-image::after': {
      content: '""',
      position: 'absolute',
      backgroundColor: 'transparent',
      zIndex: 20,
      top: '20%',
      left: '9%',
      width: '78%',
      height: '50%',
      border: '5px dashed rgba(155, 155, 155, 0.5)',
      borderRadius: '16px',
    },
    '&.dragging-image::before': {
      content: '""',
      position: 'absolute',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      zIndex: 20,
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
    },
    '&.is-over-image::after': {
      zIndex: 30,
      borderColor: 'white',
    },
    '&.findingViewerContainerDisabled': {
      pointerEvents: 'none',
    },
  },
  hoveredItem: {
    cursor: 'pointer',
    borderColor: 'white',
  },
  imageOverlay: {
    width: '100%',
    height: '96%',
    position: 'absolute',
    top: '0',
    zIndex: 10,
    left: '0',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  noImagesMessage: {
    color: 'white',
    fontWeight: 100,
    textAlign: 'center',
  },
}));

interface Props {
  children: React.ReactNode;
  currentImage: any;
  imagesPerRow: number;
  numberOfRows: number;
  imageCount?: number;
  onSetSelectedImage: React.MouseEventHandler<HTMLElement>;
  currentImageFilter: string;
  currentlyHoveredImageId: string;
  onSetCurrentlyHoveredImageId: React.Dispatch<React.SetStateAction<string>>;
  currentIndex: number;
  noImagesOnCurrentView: boolean;
  disableDrag: boolean;
  scheduledTeeth?: Set<string> | null;
  /** FMX slot indices to highlight when scheduled teeth have no image (border + "Missing image" on each). */
  expectedSlotsForMissingImage?: Set<number> | null;
}

export const ImageLayoutContainer = ({
  currentImage,
  currentImageFilter,
  imagesPerRow,
  numberOfRows,
  imageCount,
  onSetSelectedImage,
  currentlyHoveredImageId,
  onSetCurrentlyHoveredImageId,
  currentIndex,
  noImagesOnCurrentView,
  children,
  disableDrag,
  scheduledTeeth,
  expectedSlotsForMissingImage,
}: Props) => {
  const classes = useStyles();
  const { data: prefs } = usePreferences();
  const isImageQualityInsightsEnabled =
    prefs?.imageQualityInsightsInImageViewer ?? false;
  const showImageQualityFindings = useAtomValue(showImageQualityFindingsAtom);
  const imageQuality = useAtomValue(imageQualityAtom);
  const isRenderingEngineInitialized = useAtomValue(renderingEngineAtom);
  const renderingEngine =
    isRenderingEngineInitialized && getRenderingEngine(RENDERING_ENGINE);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useLingui();
  const imageFindings =
    isImageQualityInsightsEnabled && showImageQualityFindings
      ? getImageFindingsFromImageQuality(
          currentImage.imageManifestId ?? 0,
          imageQuality,
          scheduledTeeth,
        )
      : [];

  const { FindingOverlay } = useImageFindingOverlay({
    containerRef,
    imageFindings,
    renderingEngine: renderingEngine || null,
    viewportId: `fmx${currentIndex}`,
    imageId: currentImage.imageId,
    imageObjectId: currentImage.objectId,
  });

  const {
    attributes,
    listeners,
    setNodeRef,
    isOver: isDraggingOver,
    over: isDragging,
  } = useSortable({
    id: (currentImage.fmxIndex ?? 999).toString(),
    disabled: disableDrag,
  });

  const getClassNames = () => {
    let className = classes.findingViewerContainer;
    if (currentImage?.objectId === currentlyHoveredImageId) {
      className += ` ${classes.hoveredItem}`;
    }
    if (isDragging) {
      className += ' dragging-image';
    }
    if (isDraggingOver) {
      className += ' is-over-image';
    }
    // Only disable pointer events for confidence score filter, not for Series view
    // Series view should allow clicking but disable dragging
    if (disableDrag && currentImageFilter !== AnnotatorConstants.BITEWING) {
      className += ` findingViewerContainerDisabled`;
    }

    return className;
  };

  const noImagesMessageToDisplay = (imageFilter: string) => {
    if (!noImagesOnCurrentView) {
      return '';
    }
    if (imageFilter === AnnotatorConstants.BITEWING) {
      return t`No bitewing images found for this patient`;
    }
    if (imageFilter === AnnotatorConstants.FMX) {
      return t`No bitewings or periapical images found for this patient`;
    }
  };

  const isExpectedMissingSlot =
    isImageQualityInsightsEnabled &&
    showImageQualityFindings &&
    currentImageFilter === AnnotatorConstants.FMX &&
    expectedSlotsForMissingImage != null &&
    currentImage.fmxIndex != null &&
    expectedSlotsForMissingImage.has(currentImage.fmxIndex) &&
    !currentImage.objectId;

  const { maxHeight, layoutHeight } = useMemo(() => {
    let _maxHeight = 0.33;
    let _layoutHeight = 0.75;

    if (currentImageFilter === AnnotatorConstants.FMX) {
      _layoutHeight = 1 / numberOfRows;
    } else {
      _layoutHeight = 0.75 / numberOfRows;
    }

    if (imageCount) {
      switch (imageCount) {
        case 1:
          _maxHeight = 0.9;
          _layoutHeight = 0.9;
          break;
        case 2:
          _maxHeight = 0.75;
          break;
        case 3:
          _maxHeight = 0.5;
          break;
        case 4:
          _maxHeight = 0.33;
          break;
      }
    }

    return {
      maxHeight: _maxHeight,
      layoutHeight: _layoutHeight,
    };
  }, [currentImageFilter, numberOfRows, imageCount]);

  return (
    <React.Fragment key={currentImage.objectId}>
      <Box
        data-testid="image-container"
        width={1 / imagesPerRow}
        height={layoutHeight}
        maxHeight={maxHeight}
        onClick={onSetSelectedImage}
        className={getClassNames()}
        onMouseEnter={() => onSetCurrentlyHoveredImageId(currentImage.objectId)}
        onMouseLeave={() => onSetCurrentlyHoveredImageId('')}
        ref={setNodeRef}
        {...attributes}
        {...(disableDrag ? {} : listeners)}
      >
        {isExpectedMissingSlot && (
          <div className="vai-relative vai-w-full vai-h-full">
            <div
              className="vai-absolute vai-z-[100]"
              style={{ left: 2, top: 2, right: 2 }}
            >
              <ImageFindingBadge
                findings={
                  [
                    {
                      type: 'missing_image',
                      label: 'Missing image',
                      severity: 'warning',
                    },
                  ] satisfies ImageFinding[]
                }
              />
            </div>
            <div
              className="vai-absolute vai-border-2 vai-border-amber-500 vai-rounded vai-pointer-events-none vai-z-[99]"
              style={{ left: 2, top: 2, right: 2, bottom: 2 }}
            />
          </div>
        )}
        {currentImage.objectId && (
          <div
            ref={containerRef}
            className="vai-relative vai-w-full vai-h-full"
          >
            {children}
            {FindingOverlay}
          </div>
        )}
        {currentImage.objectId && (
          // Don't show the finding viewer if this is just an empty box (i.e. if we filled this index with a {})
          <div className={classes.imageOverlay} />
        )}
      </Box>

      {currentImageFilter === AnnotatorConstants.BITEWING &&
        noImagesOnCurrentView &&
        currentIndex == 0 && (
          <Box
            position="absolute"
            left="0"
            right="0"
            marginLeft="auto"
            marginRight="auto"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <h3 className={classes.noImagesMessage}>
              {noImagesMessageToDisplay(currentImageFilter)}
            </h3>
          </Box>
        )}

      {currentImageFilter === AnnotatorConstants.FMX && currentIndex === 8 && (
        // If we're looking at the 10th (index 9) image, we need to show a gap of 3/7 as spacing (for bitewing row)
        <Box
          width={3 / imagesPerRow}
          height={1 / numberOfRows}
          display="flex"
          justifyContent="center"
        >
          <Box
            width={3 / 4}
            height={0.8}
            className={classes.centerBorderedBox}
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding={1}
          >
            <h3 className={classes.noImagesMessage}>
              {noImagesMessageToDisplay(currentImageFilter)}
            </h3>
          </Box>
        </Box>
      )}
    </React.Fragment>
  );
};
